import Koppa from "@koppajs/koppajs-core";

import routes from "./routes";
import routerView from "./router-view.kpa";
import { applyRouteMetadata } from "./metadata";
import { ActiveRoute, Route, RouteName, RouterOptions } from "./types";

Koppa.take(routerView, 'router-view');

const options: RouterOptions = {
    name: 'router',
    routes: routes,
    root: '',
};

class Router {
    // private debug = false;
    public name = "router";
    private routes: Route[];
    private root: string;
    private currentRoute: ActiveRoute | null = null;
    private scrollCache = new Map<number, number>();
    private currentKey: number = Date.now();
    private ignoreNextPopState = false;

    constructor(options: RouterOptions) {
        this.routes = options.routes;
        this.root = options.root ? this.pathBuilder(options.root) : "/";
        this.init();
    }

    // private logDebug(...args: any[]): void {
    //     if (this.debug) {
    //         console.log("[Router Debug]:", ...args);
    //     }
    // }

    // private init(): void {
    //     let initialURLHandled = false;
    
    //     const handleNavigation = () => {
    //         if (!initialURLHandled) {
    //             initialURLHandled = true;
    //             const fullPath = window.location.pathname + window.location.hash + window.location.search;
    //             this.navigate(fullPath);
    //         }
    //     };
    
    //     if (document.readyState === 'complete' || document.readyState === 'interactive') {
    //         // Sofort starten, wenn DOM bereits bereit ist
    //         handleNavigation();
    //     } else {
    //         window.addEventListener("DOMContentLoaded", handleNavigation, { once: true });
    //     }
    
    //     window.onpopstate = (event) => {
    //         if (this.ignoreNextPopState) {
    //             this.ignoreNextPopState = false;
    //             return;
    //         }
    
    //         const key = event.state?.key;
    //         if (typeof key === 'number') {
    //             this.scrollCache.set(this.currentKey, window.scrollY);
    //             this.currentKey = key;
    //         }
    
    //         this.navigate(this.getCurrentPath(), true);
    //     };
    // }

    private async init(): Promise<void> {
        const waitForRouterView = (): Promise<void> =>
            new Promise((resolve) => {
                const check = () => {
                    if (document.querySelector("router-view")) {
                        resolve();
                    } else {
                        requestAnimationFrame(check);
                    }
                };
                check();
            });
    
        const handleNavigation = async () => {
            await waitForRouterView();
            const fullPath = window.location.pathname + window.location.hash + window.location.search;
            this.navigate(fullPath);
        };

        const boot = () => {
            // Wurde bereits navigiert?
            if ((window as any).__koppaRouterBooted) return;
            (window as any).__koppaRouterBooted = true;

            requestAnimationFrame(() => {
                handleNavigation();
            });
        };

        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            boot();
        } else {
            window.addEventListener("DOMContentLoaded", boot, { once: true });
        }

        window.onpopstate = (event) => {
            if (this.ignoreNextPopState) {
                this.ignoreNextPopState = false;
                return;
            }

            const key = event.state?.key;
            if (typeof key === 'number') {
                this.scrollCache.set(this.currentKey, window.scrollY);
                this.currentKey = key;
            }

            this.navigate(this.getCurrentPath(), true);
        };
    }    

    private getCurrentPath(): string {
        return this.pathBuilder(decodeURI(window.location.pathname + window.location.search).replace(this.root, ""));
    }

    private pathBuilder(...parts: string[]): string {
        return `/${parts.map(part => this.clearSlashes(part)).filter(Boolean).join("/")}`;
    }

    private clearSlashes(path: string): string {
        return path.replace(/^\/|\/$/g, "");
    }

    private matchRoute(path: string): Route | null {
        let matchedRoute: Route | null = null;

        const searchRoute = (routeList: Route[], parentPath = "") => {
            for (const route of routeList) {
                const fullPath = this.pathBuilder(parentPath, route.path);
                const regex = new RegExp(`^${fullPath.replace(/:([\w]+)/g, "(?<$1>[\\w-]+)")}$`);
                const match = path.match(regex);
                if (match) {
                    route.params = match.groups || {};
                    matchedRoute = route;
                    return;
                }
                if (route.children) {
                    searchRoute(route.children, fullPath);
                    if (matchedRoute) return;
                }
            }
        };

        searchRoute(this.routes);
        return matchedRoute;
    }

    private generateFullPath(route: Route): string {
        const buildPath = (r: Route, parentPath = ""): string => {
            for (const rootRoute of this.routes) {
                const found = this.findParentRecursive(rootRoute, r);
                if (found) {
                    return this.pathBuilder(parentPath, found.path, r.path);
                }
            }
            return this.pathBuilder(parentPath, r.path);
        };
        return buildPath(route);
    }

    private findParentRecursive(parent: Route, target: Route): Route | null {
        if (!parent.children) return null;
        if (parent.children.includes(target)) return parent;
        for (const child of parent.children) {
            const found = this.findParentRecursive(child, target);
            if (found) return found;
        }
        return null;
    }

    private findRouteByName(name: string): Route | null {
        let foundRoute: Route | null = null;

        const search = (routes: Route[]): void => {
            for (const route of routes) {
                if (route.name === name) {
                    foundRoute = route;
                    return;
                }
                if (route.children) {
                    search(route.children);
                    if (foundRoute) return;
                }
            }
        };

        search(this.routes);
        return foundRoute;
    }

    public resolve(name: RouteName, params: Record<string, string> = {}): string | null {
        const route = this.findRouteByName(name);
        if (!route) return null;

        let fullPath = this.generateFullPath(route);

        fullPath = fullPath.replace(/:([\w]+)/g, (_, key) => {
            if (params[key]) return params[key];
            console.warn(`[Router]: Missing param ":${key}" for route "${fullPath}".`);
            return `:${key}`;
        });

        return fullPath;
    }

    public async navigateByName(name: RouteName, params: Record<string, string> = {}): Promise<void> {
        const path = this.resolve(name, params);
        if (path) {
            return this.navigate(path);
        } else {
            console.warn(`[Router]: No route found with name: ${name}`);
        }
    }

    public async navigate(pathOrName: string, silent = false): Promise<void> {
        const [basePath = '', anchor] = pathOrName.split("#");
        const isPopState = silent;
        let matchedRoute = this.matchRoute(basePath);

        if (
            this.currentRoute &&
            this.currentRoute.name === matchedRoute?.name &&
            !anchor && !silent
        ) {
            // console.log('[Router]: Navigation skipped – already at target route.');
            return;
        }

        if (!matchedRoute) {
            console.warn("[Router]: Route not found for:", basePath);
            return;
        }

        // handle redirectToChild
        if (matchedRoute.meta?.["redirectToChild"] && matchedRoute.children) {
            const target = matchedRoute.children.find(child => child.name === matchedRoute.meta?.["redirectToChild"]);
            if (target) {
                const fullPath = this.generateFullPath(target);
                return this.navigate(fullPath);
            }
        }

        if (!isPopState) {
            this.scrollCache.set(this.currentKey, window.scrollY);
            this.currentKey = Date.now();
        
            const fullPath = basePath + window.location.search + (anchor ? `#${anchor}` : '');
            const currentPath = window.location.pathname + window.location.search + window.location.hash;
        
            if (fullPath === currentPath) {
                history.replaceState({ key: this.currentKey }, '', fullPath);
            } else {
                history.pushState({ key: this.currentKey }, '', fullPath);
            }
        }

        // Stelle sicher, dass currentRoute das finale Ziel ist (nach Redirects)
        const finalRoute = matchedRoute.meta?.["redirectToChild"] && matchedRoute.children
            ? matchedRoute.children.find(child => child.name === matchedRoute.meta?.["redirectToChild"]) || matchedRoute
            : matchedRoute;

        this.currentRoute = {
            ...finalRoute,
            redirectedFrom: matchedRoute !== finalRoute ? matchedRoute : undefined
        };

        applyRouteMetadata(finalRoute.meta);
        await this.updateView(finalRoute.component);

        if (silent) {
            const scrollY = this.scrollCache.get(this.currentKey);
            if (typeof scrollY === 'number') {
                window.scrollTo({ top: scrollY, behavior: 'auto' });
                return;
            }
        }
        
        if (anchor) {
            const anchorEl = document.getElementById(anchor);
            if (anchorEl) {
                anchorEl.scrollIntoView({ behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'auto' });
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'auto' });
        }

        const routeChangeEvent = new CustomEvent("routechange", {
            detail: {
                path: window.location.pathname,
                hash: window.location.hash,
                fullPath: window.location.pathname + window.location.search + window.location.hash,
                route: this.currentRoute
            },
        });
        window.dispatchEvent(routeChangeEvent);
    }

    private updateView(component: string | null): Promise<void> {
        return new Promise((resolve) => {
            const tryRender = (attempts = 5) => {
                const routerView = document.querySelector("router-view");
                if (!routerView) {
                    if (attempts > 0) {
                        return setTimeout(() => tryRender(attempts - 1), 10);
                    } else {
                        console.warn("[Router]: No <router-view> found in the DOM after multiple attempts.");
                        return resolve();
                    }
                }

                if (routerView.firstElementChild?.tagName?.toLowerCase?.() !== component?.toLowerCase?.()) {
                    routerView.innerHTML = component
                        ? `<${component}></${component}>`
                        : "<p>Component not found</p>";
                }

                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        resolve();
                    });
                });
            };

            tryRender();
        });
    }

    public addRoute(route: Route): void {
        this.routes.push(route);
    }

    public getCurrentRouteMeta(): Record<string, any> | null {
        return this.currentRoute?.meta || null;
    }

    public getRouteMeta(path: string): Record<string, any> | null {
        const matchedRoute = this.matchRoute(path);
        return matchedRoute ? matchedRoute.meta || null : null;
    }
}

export default new Router(options);
